import { Observable } from "rxjs"

import {
    ROLE_ITEM_SAVE_RESPONSE,
    STAKEHOLDERS_PAGE_ITEM_REQUEST,
    STAKEHOLDERS_PAGE_ITEM_RESPONSE,
    STAKEHOLDERS_PAGE_LIST_REQUEST,
    STAKEHOLDERS_PAGE_LIST_RESPONSE,
    STAKEHOLDER_APPLY_FILTER_CRITERIA,
    STAKEHOLDER_APPLY_MODEL_UPDATE,
    STAKEHOLDER_CANCEL_MODEL_UPDATE,
    STAKEHOLDER_FINISH_SAVING,
    STAKEHOLDER_ITEM_CACHED_RESPONSE,
    STAKEHOLDER_ITEM_REQUEST,
    STAKEHOLDER_ITEM_RESPONSE,
    STAKEHOLDER_ITEM_SAVE_REQUEST,
    STAKEHOLDER_ITEM_SAVE_RESPONSE,
    STAKEHOLDER_LIST_CACHED_RESPONSE,
    STAKEHOLDER_LIST_ERROR_RESPONSE,
    STAKEHOLDER_LIST_REQUEST,
    STAKEHOLDER_LIST_RESPONSE,
    STAKEHOLDER_LIST_SEARCH_REQUEST,
} from "../actions/action-types"
import { addCustomErrorAlert } from "../actions/alert-actions"
import {
    callbackActionCreatorEpic,
    requestItemDeleteSubresourceHandler,
    requestItemEpic,
    requestItemSaveEpic,
    requestItemSaveSubresourceHandler,
    requestListEpic,
    requestListFilterEpic,
    requestListSearchEpic,
    requestTopItemFromListResponseEpic,
    syncIdToURLEpic,
} from "./default-epics/default-entity-epics"
import { defaultSearchCriteria } from "../reducers/paging/stakeholder-paging-reducer"
import { deleteEvidenceItem, postEvidenceItem } from "../api/evidence-api"
import { deleteRoleItem, patchRoleItem, putRoleItem } from "../api/role-api"
import {
    evidenceItemDeleteRequest,
    evidenceItemDeleteResponse,
    evidenceItemErrorResponse,
    evidenceItemSaveRequest,
    evidenceItemSaveResponse,
} from "../actions/evidence-actions"
import {
    getActionsToReplay,
    mapRequestError,
    waitForAll,
} from "./default-epics/epic-utils"
import {
    getStakeholderItem,
    getStakeholderList,
    patchStakeholderItem,
    postStakeholderItem,
} from "../api/stakeholder-api"
import {
    hydrate as hydrateStakeholder,
    normalisingFields,
} from "../models/entities/stakeholder"
import {
    roleItemDeleteRequest,
    roleItemDeleteResponse,
    roleItemErrorResponse,
    roleItemSaveRequest,
    roleItemSaveResponse,
} from "../actions/role-actions"
import {
    stakeholderFinishSaving,
    stakeholderFinishSavingError,
    stakeholderItemCachedResponse,
    stakeholderItemErrorResponse,
    stakeholderItemRequest,
    stakeholderItemResponse,
    stakeholderItemSaveRequest,
    stakeholderItemSaveResponse,
    stakeholderListCachedResponse,
    stakeholderListErrorResponse,
    stakeholderListRequest,
    stakeholderListResponse,
    stakeholderShowInvalidFields,
    stakeholdersPageItemRequest,
    stakeholdersPageListRequest,
} from "../actions/stakeholder-actions"
import { unauthenticated } from "../actions/authentication-actions"

export const requestStakeholderListEpic = requestListEpic(
    STAKEHOLDER_LIST_REQUEST,
    getStakeholderList,
    "stakeholders",
    stakeholderListResponse,
    stakeholderListCachedResponse,
    stakeholderListErrorResponse
)

export const requestStakeholderListPageEpic = (action$) =>
    action$
        .ofType(STAKEHOLDERS_PAGE_LIST_REQUEST)
        .map(({ type, ...pagingRequest }) =>
            stakeholderListRequest(pagingRequest)
        )

export const requestStakeholderSearchEpic = requestListSearchEpic(
    STAKEHOLDER_LIST_SEARCH_REQUEST,
    stakeholdersPageListRequest,
    "stakeholders"
)

export const applyStakeholderFilterCriteria = requestListFilterEpic(
    STAKEHOLDER_APPLY_FILTER_CRITERIA,
    stakeholdersPageListRequest,
    "stakeholders"
)

export const requestStakeholderItemEpic = requestItemEpic(
    STAKEHOLDER_ITEM_REQUEST,
    getStakeholderItem,
    "stakeholders",
    hydrateStakeholder,
    normalisingFields,
    stakeholderItemResponse,
    stakeholderItemCachedResponse,
    stakeholderItemErrorResponse
)

export const requestStakeholderCancelModelUpdateEpic = (
    action$,
    { getState }
) =>
    action$
        .ofType(STAKEHOLDER_CANCEL_MODEL_UPDATE)
        .map(() => getState().presentation.stakeholders.activeId)
        .filter((id) => id)
        .map(stakeholderItemRequest)

export const requestStakeholderApplyModelUpdateEpic = (action$, { getState }) =>
    action$.ofType(STAKEHOLDER_APPLY_MODEL_UPDATE).mergeMap(() => {
        const {
            activeId,
            updateModel,
            imageToUpload,
        } = getState().presentation.stakeholders

        const roles = getState().presentation.roles.edit

        let newUpdateModel = activeId
            ? updateModel.set("id", activeId)
            : updateModel

        if (roles.length === 0) {
            return Observable.of(
                stakeholderShowInvalidFields(),
                addCustomErrorAlert(
                    "The stakeholder must have at least one relationship",
                    "Save error"
                )
            )
        } else {
            return Observable.of(
                stakeholderItemSaveRequest(
                    activeId,
                    newUpdateModel.toJS(),
                    imageToUpload
                )
            )
        }
    })

export const requestStakeholderItemSaveEpic = requestItemSaveEpic(
    STAKEHOLDER_ITEM_SAVE_REQUEST,
    patchStakeholderItem,
    postStakeholderItem,
    "stakeholder",
    stakeholderItemSaveResponse,
    stakeholderItemErrorResponse
)

export const responseStakeholderItemSaveEpic = (action$, { getState }) =>
    action$.ofType(STAKEHOLDER_FINISH_SAVING).map((action) => {
        const state = getState()
        const string = state.presentation.stakeholders.searchCriteria
        const pagingRequest = state.paging.stakeholders[string]
        return stakeholdersPageListRequest(pagingRequest)
    })

export const requestStakeholderPageItemEpic = (action$, { getState }) =>
    action$
        .ofType(STAKEHOLDERS_PAGE_ITEM_REQUEST)
        .map((action) =>
            stakeholderItemRequest(
                action.id,
                false,
                action.callbackActionCreator
            )
        )

export const responseStakeholderPageItemEpic = (action$, { getState }) =>
    action$
        .ofType(STAKEHOLDER_ITEM_RESPONSE, STAKEHOLDER_ITEM_CACHED_RESPONSE)
        .filter(({ callbackActionCreator }) => callbackActionCreator)
        .map(({ callbackActionCreator, data }) => callbackActionCreator(data))

export const responseStakeholderListEpic = requestTopItemFromListResponseEpic(
    STAKEHOLDERS_PAGE_LIST_RESPONSE,
    stakeholdersPageItemRequest,
    "stakeholders",
    "stakeholders",
    defaultSearchCriteria
)

export const responseStakeholderCallbackEpic = callbackActionCreatorEpic(
    STAKEHOLDER_LIST_RESPONSE,
    STAKEHOLDER_LIST_CACHED_RESPONSE,
    STAKEHOLDER_LIST_ERROR_RESPONSE
)

export const syncStakeholderIdToURLEpic = syncIdToURLEpic(
    "stakeholders",
    STAKEHOLDERS_PAGE_ITEM_RESPONSE,
    STAKEHOLDER_ITEM_SAVE_RESPONSE
)

const roleSaveHandler = requestItemSaveSubresourceHandler(
    patchRoleItem,
    putRoleItem,
    roleItemSaveResponse,
    roleItemErrorResponse
)

const roleDeleteHandler = requestItemDeleteSubresourceHandler(
    deleteRoleItem,
    "role",
    roleItemDeleteResponse,
    roleItemErrorResponse
)

export const saveStakeholderRolesEpic = (action$, { getState }) =>
    action$.ofType(STAKEHOLDER_ITEM_SAVE_RESPONSE).mergeMap((action) => {
        const { stakeholders } = getState().presentation

        const stakeholderID = action.data.id

        const roleEntities = getState().entities.roles

        const roles = Object.keys(roleEntities).map(
            (roleID) => roleEntities[roleID]
        )

        const { workingModel } = stakeholders

        const putRoleActions = roles
            .filter((role) => role.saveStatus === "PUT")
            .map((role) => {
                // remove fake timestamp id at this point to not confuse the save epic
                const { id, ...roleToSave } = role
                return roleItemSaveRequest(stakeholderID, roleToSave)
            })
        const patchRoleActions = roles
            .filter((role) => role.saveStatus === "PATCH")
            .map((role) => {
                return roleItemSaveRequest(stakeholderID, role)
            })
        const deleteRoleActions = roles
            .filter((role) => role.saveStatus === "DELETE")
            .map((role) => roleItemDeleteRequest(stakeholderID, role.id))

        const saveRoleActions = [...putRoleActions, ...patchRoleActions]

        const allRoleActions = [
            ...saveRoleActions.map((action) =>
                roleSaveHandler(getState, action)
            ),
            ...deleteRoleActions.map((action) =>
                roleDeleteHandler(getState, action)
            ),
        ]

        return waitForAll(allRoleActions).mergeMap((roleResponseActions) => {
            const { evidence } = getState().presentation

            /*
               If there has been a roleSaveAction, we need to swap the fakeIDs
               for actual IDs coming from the response.
               Therefore, replace each fake id in presentation.evidence for
               the correct role id from saveRoleResponse
              */

            const roleSaveResponseActions = roleResponseActions.filter(
                (action) => action.type === ROLE_ITEM_SAVE_RESPONSE
            )

            const evidenceItems = Object.keys(evidence)
                .map((roleID) => {
                    const items = evidence[roleID].items || []

                    roleID = fixRoleID(
                        roleID,
                        roleEntities,
                        roleSaveResponseActions
                    )

                    return items.map((item) => ({
                        roleID,
                        item,
                    }))
                })
                .reduce((accumulatedArray, currentArray) => [
                    ...accumulatedArray,
                    ...currentArray,
                ])

            const evidenceSaveActions = evidenceItems
                .filter(
                    ({ item, roleID }) =>
                        item.saveStatus === "POST" && roleID.length > 11
                )
                .map(({ roleID, item }) =>
                    evidenceItemSaveRequest(item, stakeholderID, roleID)
                )
            const evidenceDeleteActions = evidenceItems
                .filter(({ item }) => item.saveStatus === "DELETE")
                .map(({ roleID, item }) =>
                    evidenceItemDeleteRequest(item.id, stakeholderID, roleID)
                )

            const { token } = getState().authentication
            const evidenceSaveResponseActions = evidenceSaveActions.map(
                ({ stakeholderID, roleID, item }) =>
                    postEvidenceItem(token, stakeholderID, roleID, item)
                        .map((data) => evidenceItemSaveResponse(data, roleID))
                        .catch(handleEvidenceError)
            )
            const evidenceDeleteResponseActions = evidenceDeleteActions.map(
                ({ stakeholderID, roleID, evidenceID }) =>
                    deleteEvidenceItem(token, stakeholderID, roleID, evidenceID)
                        .map((data) =>
                            evidenceItemDeleteResponse(evidenceID, roleID)
                        )
                        .catch(handleEvidenceError)
            )

            const evidenceAllResponseActions = [
                ...evidenceSaveResponseActions,
                ...evidenceDeleteResponseActions,
            ]

            return waitForAll(evidenceAllResponseActions).mergeMap(
                (evidenceResponseActions) => {
                    const actionsToReplay = getActionsToReplay(
                        [...roleResponseActions, ...evidenceResponseActions],
                        [...saveRoleActions, ...evidenceSaveActions],
                        [...deleteRoleActions, ...evidenceDeleteActions],
                        stakeholderFinishSaving,
                        stakeholderFinishSavingError,
                        "stakeholder",
                        workingModel.full_name
                    )

                    return Observable.of(...actionsToReplay)
                }
            )
        })
    })

function handleEvidenceError(error) {
    return Observable.of(
        mapRequestError(error, unauthenticated, evidenceItemErrorResponse)
    )
}

function fixRoleID(roleID, roleEntities, roleSaveResponseActions) {
    // if roleID is fake (aka if it's only numbers, no letters)
    if (!roleID.match(/[a-z]/i)) {
        const { linked_node, name } = roleEntities[roleID]

        // map to the right response action, but ignore delete actions
        const matchingResponseAction = roleSaveResponseActions.find(
            ({ item }) =>
                item.linked_node.id === linked_node.id && item.name === name
        )

        // add the items in question under the actual roleID key
        if (matchingResponseAction) {
            return matchingResponseAction.item.id
        }
    }

    return roleID
}
